import Cookies from "js-cookie";
import browserCookieDomain from "./browserCookieDomain";
import duplicateNames from "./duplicateNames";

// The scope (domain, path) is the package's to decide, never the caller's.
export type CookieAttributes = {
  expires?: Date | number;
  secure?: boolean;
  sameSite?: "strict" | "lax" | "none";
};

// Like js-cookie, every helper is a no-op while a client component renders on
// the server, so none of them can break a prerender. In the browser, the first
// call of the page tidies duplicates first.
const inBrowser = () => {
  if (typeof document === "undefined") return false;
  expireDuplicatesOnce();
  return true;
};

export const getCookie = (name: string): string | undefined => {
  if (!inBrowser()) return undefined;
  return Cookies.get(name);
};

export const getCookies = (): Record<string, string> => {
  if (!inBrowser()) return {};
  return Cookies.get() ?? {};
};

export const setCookie = (
  name: string,
  value: string,
  attributes: CookieAttributes = {},
) => {
  if (!inBrowser()) return;
  const domain = browserCookieDomain();
  if (domain) expireHostCopies(name, domain);
  Cookies.set(name, value, { ...attributes, path: "/", domain });
};

export const removeCookie = (name: string) => {
  if (!inBrowser()) return;
  const domain = browserCookieDomain();
  expireHostCopies(name, domain);
  if (domain) Cookies.remove(name, { path: "/", domain });
};

let duplicatesExpired = false;

// A page served from the CDN never passed the proxy's duplicate check, so the
// browser runs it too, once per page, before the first read or write.
const expireDuplicatesOnce = () => {
  if (duplicatesExpired) return;
  duplicatesExpired = true;
  const domain = browserCookieDomain();
  if (!domain) return;
  for (const name of duplicateNames(document.cookie)) {
    expireHostCopies(name, domain);
  }
};

export const resetDuplicateCheck = () => {
  duplicatesExpired = false;
};

// A copy written before cookies moved to the shared domain (host-only, or on
// the host's own name) would sit beside the shared one, and readers disagree
// on which of the two wins.
const expireHostCopies = (name: string, domain: string | undefined) => {
  Cookies.remove(name, { path: "/" });
  const { hostname } = window.location;
  if (hostname !== domain)
    Cookies.remove(name, { path: "/", domain: hostname });
};
